import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import mapboxgl from 'mapbox-gl';
import { getFlagUrl } from '../utils/countryVaultUtils';
import { iso3ToIso2, iso2ToNumeric } from '../countryCodes';

// ── Colour palette ────────────────────────────────────────────────────────────
const C = {
  bg: '#fff', border: '#dde3ea', text: '#1a1f2e',
  textMuted: '#5a6376', textFaint: '#8a93a6',
  gold: '#FFD700', goldDark: '#996600',
  green: '#28a745', red: '#dc3545', blue: '#0d6efd', amber: '#ff9800',
};

// ── Contract marker styles — identical to App.tsx ─────────────────────────────
const RULE_GRADIENTS: Record<string, string> = {
  location:  'linear-gradient(135deg,#7B68EE 0%,#9d8fff 100%)',
  proximity: 'linear-gradient(135deg,#4A90D9 0%,#6aaef0 100%)',
  geofence:  'linear-gradient(135deg,#FF6B35 0%,#ff8c60 100%)',
};
const RULE_BORDERS: Record<string, string> = {
  location: '#b8adff', proximity: '#7bb9f5', geofence: '#ffaa87',
};

function makeContractMarkerEl(ruleType: string, autoExecute = false): HTMLElement {
  const el  = document.createElement('div');
  const bg  = RULE_GRADIENTS[ruleType] || RULE_GRADIENTS.location;
  const bdr = autoExecute ? '#FFD700' : (RULE_BORDERS[ruleType] || RULE_BORDERS.location);
  const shd = autoExecute ? 'rgba(255,215,0,0.7)' : 'rgba(99,102,241,0.5)';
  el.style.cssText = `
    width:40px;height:40px;background:${bg};
    border-radius:6px;border:2px solid ${bdr};
    box-shadow:0 4px 12px ${shd};
    display:flex;align-items:center;justify-content:center;
    font-size:20px;color:white;cursor:default;
    position:relative;transform:translate(-50%,-50%);pointer-events:none;
  `;
  el.innerHTML = '🧮';
  return el;
}

function makeUserMarkerEl(): HTMLElement {
  const el = document.createElement('div');
  // Do NOT set transform here — Mapbox manages the element's transform to
  // position it on the map (anchor:'center' already centres it).  A
  // duplicate translate(-50%,-50%) in cssText conflicts and causes floating.
  el.style.cssText = `
    width:24px;height:24px;border-radius:50%;
    background:#FFD700;border:3px solid white;
    box-shadow:0 4px 12px rgba(0,0,0,0.4);
    cursor:default;animation:rmd-pulse 2s infinite;
  `;
  el.title = 'Your GPS location';
  return el;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371, toR = (d: number) => (d * Math.PI) / 180;
  const dLat = toR(lat2 - lat1), dLng = toR(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(toR(lat1)) * Math.cos(toR(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
function fmtDist(km: number) { return km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(2)} km`; }
function fmtSec(s: number) {
  if (s < 60) return `${s}s`; if (s < 3600) return `${Math.round(s / 60)}m`;
  if (s < 86400) return `${(s / 3600).toFixed(1)}h`; return `${(s / 86400).toFixed(1)}d`;
}
function makeCircle(lng: number, lat: number, r: number, pts = 64) {
  const km = r / 1000, dx = km / (111.32 * Math.cos((lat * Math.PI) / 180)), dy = km / 110.574;
  const coords = Array.from({ length: pts + 1 }, (_, i) => {
    const a = (i / pts) * 2 * Math.PI; return [lng + dx * Math.cos(a), lat + dy * Math.sin(a)];
  });
  return { type: 'Feature', geometry: { type: 'Polygon', coordinates: [coords] }, properties: {} };
}

// Always fetch fresh from the API endpoint so border edits made by admin are
// immediately visible the next time this overlay opens (no stale static-file cache).
async function loadCountriesGeoJson(): Promise<any> {
  try {
    const r = await fetch('/api/countries', { headers: { 'Cache-Control': 'no-cache' } });
    return await r.json();
  } catch { return null; }
}

/**
 * Stamps each feature with an `allowed` boolean derived from the allowedCountries set.
 * When allowedCountries is undefined/empty the property is omitted so the Mapbox
 * style expression falls through to the neutral grey fallback — borders still render.
 */
function buildCountriesData(raw: any, allowedCountries?: Set<number>): any {
  const hasPolicy = allowedCountries != null && allowedCountries.size > 0;
  return {
    ...raw,
    features: (raw.features as any[]).map((f: any) => {
      if (!hasPolicy) return f; // no policy — keep original properties, grey fallback style applies
      const iso3    = typeof f.id === 'string' ? f.id : (f.id != null ? String(f.id) : '');
      const iso2    = iso3 ? iso3ToIso2(iso3) : '';
      const numeric = iso2 ? iso2ToNumeric(iso2) : 0;
      return { ...f, properties: { ...f.properties, allowed: numeric > 0 && allowedCountries!.has(numeric) } };
    }),
  };
}

/** Adds / replaces the countries source+layers on a fully-loaded map. */
function applyCountryLayers(m: mapboxgl.Map, data: any): void {
  try {
    if (m.getSource('countries')) {
      (m.getSource('countries') as mapboxgl.GeoJSONSource).setData(data);
    } else {
      m.addSource('countries', { type: 'geojson', data });
      m.addLayer({
        id: 'countries-fill', type: 'fill', source: 'countries',
        paint: {
          'fill-color': ['case', ['==', ['typeof', ['get', 'allowed']], 'boolean'],
            ['case', ['get', 'allowed'], 'rgba(0,255,0,0.12)', 'rgba(255,0,0,0.06)'],
            'rgba(128,128,128,0.05)'],
          'fill-opacity': 0.4,
        },
      });
      m.addLayer({
        id: 'countries-outline', type: 'line', source: 'countries',
        paint: {
          'line-color': ['case', ['==', ['typeof', ['get', 'allowed']], 'boolean'],
            ['case', ['get', 'allowed'], '#00CC55', '#FF3333'],
            '#888888'],
          'line-width': 1.2, 'line-opacity': 0.75,
        },
      });
    }
  } catch { /* concurrent style change — ignore */ }
}

let cssInjected = false;
function injectCSS() {
  if (cssInjected) return; cssInjected = true;
  const s = document.createElement('style');
  s.textContent = `
    @keyframes rmd-pulse {
      0%  { box-shadow: 0 0 0 0   rgba(255,215,0,.7); }
      70% { box-shadow: 0 0 0 10px rgba(255,215,0,0); }
      100%{ box-shadow: 0 0 0 0   rgba(255,215,0,0); }
    }
    .rmd-map .mapboxgl-ctrl-logo { display: none !important; }
    .rmd-map .mapboxgl-ctrl-attrib-button { display: none !important; }
    .rmd-map .mapboxgl-ctrl-attrib.mapboxgl-compact { background: transparent !important; }
    .rmd-map .mapboxgl-ctrl-attrib.mapboxgl-compact-show { display: none !important; }
    .rmd-country-popup .mapboxgl-popup-content {
      background: rgba(8,13,24,0.97) !important;
      border: 1px solid rgba(255,255,255,0.12) !important;
      border-left: 3px solid #4CAF50 !important;
      border-radius: 10px !important;
      padding: 0 !important;
      box-shadow: 0 8px 32px rgba(0,0,0,0.6) !important;
      overflow: hidden;
    }
    .rmd-country-popup .mapboxgl-popup-tip { display: none !important; }
  `;
  document.head.appendChild(s);
}

// ── Types ─────────────────────────────────────────────────────────────────────
export interface RuleMatchInfo {
  ruleId:           string | number;
  ruleName?:        string | null;
  contractAddress?: string | null;
  functionName?:    string | null;
  network?:         string | null;
  ruleLocation?:    { latitude: number; longitude: number } | null;
  matchedAt?:       string | null;
  status?:          'pending' | 'completed' | 'rejected';
  transactionHash?: string | null;
  completedAt?:     string | null;
  rejectedAt?:      string | null;
  rejectedReason?:  string | null;
  amount?:          string | null;
  // Passed from the nearby-contract data so the overlay has the right type/radius
  // immediately — before the full-rule fetch completes.
  ruleType?:        string | null;
  radiusMeters?:    number | null;
  boundaryGeoJson?: { type: string; coordinates: any[] } | null;
}
interface FullRule {
  id?: number; name?: string; rule_type?: string; trigger_on?: string;
  center_latitude?: number; center_longitude?: number; radius_meters?: number;
  auto_execute?: boolean; requires_confirmation?: boolean;
  max_executions_per_public_key?: number; execution_time_window_seconds?: number;
  min_location_duration_seconds?: number; is_active?: boolean; created_at?: string;
}
interface HoveredCountry { name: string; iso2: string; x: number; y: number; }

interface Props {
  isOpen: boolean; onClose: () => void;
  info: RuleMatchInfo; network?: 'testnet' | 'mainnet';
  allowedCountries?: Set<number>;
  /** Incremented when admin saves border changes — triggers a live re-fetch */
  borderRevision?: number;
}

// ── Component ─────────────────────────────────────────────────────────────────
export const RuleMatchDetailOverlay: React.FC<Props> = ({ isOpen, onClose, info, network = 'testnet', allowedCountries, borderRevision = 0 }) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef          = useRef<mapboxgl.Map | null>(null);

  // Always-fresh position refs (fixes stale-closure in zoom callbacks)
  const rulePosRef = useRef<{ lat: number; lng: number } | null>(null);
  const userPosRef = useRef<{ lat: number; lng: number } | null>(null);
  // Setter ref so map event listeners can update React state without stale closure
  const setHovCtryRef  = useRef<React.Dispatch<React.SetStateAction<HoveredCountry | null>> | null>(null);
  const hovTimerRef    = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Refs for values needed by setupMapLayers (avoids stale closures in callbacks)
  const ruleLatRef          = useRef<number | null>(null);
  const ruleLngRef          = useRef<number | null>(null);
  const ruleRRef            = useRef<number>(500);
  const ruleTypeRef         = useRef<string>('proximity');
  const autoExecRef         = useRef<boolean>(false);
  const allowedCountriesRef = useRef<Set<number> | undefined>(undefined);
  const isDarkRef           = useRef<boolean>(false);
  // Marker refs so they can be cleaned up on style change
  const contractMarkerRef   = useRef<mapboxgl.Marker | null>(null);
  const userMarkerRef       = useRef<mapboxgl.Marker | null>(null);

  const [userPos,          setUserPos]          = useState<{ lat: number; lng: number } | null>(null);
  const [fullRule,         setFullRule]          = useState<FullRule | null>(null);
  const [hoveredCountry,   setHoveredCountry]    = useState<HoveredCountry | null>(null);
  const [overlayMapStyle,  setOverlayMapStyle]   = useState<'light' | 'dark'>('light');
  const [countriesGeoJson, setCountriesGeoJson]  = useState<any>(null);
  const countriesGeoJsonRef = useRef<any>(null);

  const MAPBOX_TOKEN = (mapboxgl as any).accessToken || process.env.REACT_APP_MAPBOX_TOKEN || '';

  // Keep setter ref fresh on every render so map callbacks never go stale
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setHovCtryRef.current = setHoveredCountry; });

  // Keep refs in sync with latest values (non-derived values)
  useEffect(() => { userPosRef.current = userPos; }, [userPos]);
  useEffect(() => { allowedCountriesRef.current = allowedCountries; }, [allowedCountries]);
  useEffect(() => { isDarkRef.current = overlayMapStyle === 'dark'; }, [overlayMapStyle]);
  useEffect(() => { countriesGeoJsonRef.current = countriesGeoJson; }, [countriesGeoJson]);

  // Stable helper — adds/replaces all map layers.  Uses refs exclusively so it
  // never needs to be recreated (avoids stale-closure issues in style.load callbacks).
  const setupMapLayers = useCallback((m: mapboxgl.Map) => {
    const lat  = ruleLatRef.current;
    const lng  = ruleLngRef.current;
    const r    = ruleRRef.current;
    const rt   = ruleTypeRef.current;
    const ae   = autoExecRef.current;
    const uPos = userPosRef.current;
    const dark = isDarkRef.current;

    // --- Remove old markers ---
    contractMarkerRef.current?.remove(); contractMarkerRef.current = null;
    userMarkerRef.current?.remove();     userMarkerRef.current     = null;

    // --- Remove old custom layers/sources ---
    // Note: countries-fill / countries-outline / countries source are managed by the
    // dedicated applyCountryLayers() effect and are NOT cleaned up here.  They survive
    // setupMapLayers() calls and are only rebuilt via applyCountryLayers().
    (['rule-circle-fill','rule-circle-outline','dotted-line-bg','dotted-line','3d-buildings'] as const).forEach(id => {
      try { if (m.getLayer(id)) m.removeLayer(id); } catch {}
    });
    (['rule-circle','dotted-line'] as const).forEach(id => {
      try { if (m.getSource(id)) m.removeSource(id); } catch {}
    });

    // --- 3-D buildings (dark only) ---
    if (dark) {
      // Mapbox-gl-js #11820: fill-extrusion layers added inside a
      // `styledata`/`style.load` event chain (which is exactly how
      // this overlay's setupMapLayers gets invoked) can leave the
      // layer's internal Layout uninstantiated, crashing the very
      // next render at `layer.layout.get('fill-extrusion-edge-
      // radius')`. Defer one macrotask so Mapbox finishes its style
      // recalculation before the new fill-extrusion layer drops in.
      setTimeout(() => {
        try {
          if (!m || m.getLayer('3d-buildings')) return;
          m.addLayer({
            id: '3d-buildings', source: 'composite', 'source-layer': 'building',
            filter: ['==', 'extrude', 'true'], type: 'fill-extrusion', minzoom: 12,
            // Explicit empty layout — see comment above.
            layout: {},
            paint: {
              'fill-extrusion-color': '#1a2035',
              'fill-extrusion-height': ['get', 'height'],
              'fill-extrusion-base':   ['get', 'min_height'],
              'fill-extrusion-opacity': 0.6,
            },
          });
        } catch {}
      }, 0);
      try { m.setFog({ range: [0.5, 10], color: '#0a0f1e', 'horizon-blend': 0.05 }); } catch {}
    }

    // --- Rule geofence circle ---
    if (lat != null && lng != null) {
      const geo = makeCircle(lng, lat, r);
      const col = RULE_BORDERS[rt] || RULE_BORDERS.location;
      try {
        m.addSource('rule-circle', { type: 'geojson', data: geo as any });
        m.addLayer({ id: 'rule-circle-fill',    type: 'fill', source: 'rule-circle', paint: { 'fill-color': col, 'fill-opacity': 0.2 } });
        m.addLayer({ id: 'rule-circle-outline', type: 'line', source: 'rule-circle', paint: { 'line-color': col, 'line-width': 2.5 } });
      } catch {}

      const cEl = makeContractMarkerEl(rt, ae);
      contractMarkerRef.current = new mapboxgl.Marker({ element: cEl, anchor: 'center' })
        .setLngLat([lng, lat]).addTo(m);
    }

    // --- User position ---
    if (uPos) {
      const uEl = makeUserMarkerEl();
      userMarkerRef.current = new mapboxgl.Marker({ element: uEl, anchor: 'center' })
        .setLngLat([uPos.lng, uPos.lat]).addTo(m);

      if (lat != null && lng != null) {
        const lineColor = dark ? '#fff' : '#1a1f2e';
        try {
          m.addSource('dotted-line', {
            type: 'geojson',
            data: {
              type: 'Feature',
              geometry: { type: 'LineString', coordinates: [[uPos.lng, uPos.lat], [lng, lat]] },
              properties: {},
            } as any,
          });
          m.addLayer({ id: 'dotted-line-bg', type: 'line', source: 'dotted-line', paint: { 'line-color': dark ? '#000' : '#ccc', 'line-width': 4, 'line-opacity': 0.2 } });
          m.addLayer({ id: 'dotted-line', type: 'line', source: 'dotted-line', paint: { 'line-color': lineColor, 'line-width': 2, 'line-dasharray': [2, 3], 'line-opacity': 0.85 } });
        } catch {}
        const bounds = new mapboxgl.LngLatBounds();
        bounds.extend([uPos.lng, uPos.lat]);
        bounds.extend([lng, lat]);
        m.fitBounds(bounds, { padding: 90, maxZoom: 14 });
      }
    }

    // Country borders are added by the dedicated countriesGeoJson+allowedCountries effect
    // (not here) to avoid async race conditions during style changes.
  }, []); // stable: all data from refs

  // Fetch full rule details from GeoLink.
  // Skip when info.ruleType is already known (it comes from the nearby-contracts
  // response and contains everything we need — radius, type, location).
  // This avoids a noisy 404 when the service API key can't read another user's rule.
  useEffect(() => {
    if (!isOpen || !info.ruleId) return;
    // We already have the essential fields from the nearby-contracts data.
    if (info.ruleType && info.radiusMeters != null) return;
    setFullRule(null);
    const ctrl = new AbortController();
    fetch(`/api/v2/geolink/rules/${encodeURIComponent(info.ruleId)}`, { signal: ctrl.signal })
      .then(r => r.json())
      .then(d => {
        const rule = d?.rule || d;
        if (rule?.center_latitude != null || rule?.id) setFullRule(rule);
      })
      .catch(() => { /* silently ignore 404 / network errors */ });
    return () => ctrl.abort();
  }, [isOpen, info.ruleId, info.ruleType, info.radiusMeters]);

  // GPS
  useEffect(() => {
    if (!isOpen) return;
    injectCSS();
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      pos => setUserPos({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {}, { maximumAge: 60_000, timeout: 8_000 },
    );
  }, [isOpen]);

  // ── Load countries GeoJSON at component level (not inside async map setup) ──
  // Clear on close so the NEXT open always re-fetches (prevents stale data from
  // a previous session being painted before the new fetch resolves).
  useEffect(() => {
    if (!isOpen) {
      setCountriesGeoJson(null);
      countriesGeoJsonRef.current = null;
      return;
    }
    loadCountriesGeoJson().then(d => { if (d) setCountriesGeoJson(d); }).catch(() => {});
  }, [isOpen]);

  // Re-fetch when admin saves border changes (borderRevision bumps) — works
  // even while the overlay is open without needing a close/reopen cycle.
  useEffect(() => {
    if (borderRevision === 0 || !isOpen) return;
    loadCountriesGeoJson().then(d => { if (d) setCountriesGeoJson(d); }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [borderRevision]);

  // ── Apply country borders whenever GeoJSON + map are ready ──────────────────
  // allowedCountries is optional — if absent, borders still render in grey
  useEffect(() => {
    const m = mapRef.current;
    if (!countriesGeoJson || !m) return;
    const data = buildCountriesData(countriesGeoJson, allowedCountries);
    if (m.isStyleLoaded()) {
      applyCountryLayers(m, data);
    } else {
      const onLoad = () => applyCountryLayers(m, data);
      m.once('style.load', onLoad);
      return () => { m.off('style.load', onLoad); };
    }
  }, [countriesGeoJson, allowedCountries]);

  // Derived values — coerce to Number because the GeoLink API sometimes returns
  // lat/lng as strings, which would break .toFixed() and Mapbox coordinate calls.
  const _rawLat  = fullRule?.center_latitude  ?? info.ruleLocation?.latitude  ?? null;
  const _rawLng  = fullRule?.center_longitude ?? info.ruleLocation?.longitude ?? null;
  const ruleLat  = _rawLat  != null ? Number(_rawLat)  : null;
  const ruleLng  = _rawLng  != null ? Number(_rawLng)  : null;
  // Use info.radiusMeters (from the nearby-contract data) as the initial value
  // so the geofence circle is correct immediately.  fullRule.radius_meters takes
  // over once the full-rule fetch completes (via the radius-update effect).
  const ruleR    = fullRule?.radius_meters != null
    ? Number(fullRule.radius_meters)
    : info.radiusMeters != null
      ? Number(info.radiusMeters)
      : 500;
  // Same for rule_type: use info.ruleType first so the marker colour matches the
  // main-map marker before fullRule arrives.
  const ruleType = (fullRule?.rule_type ?? info.ruleType ?? 'proximity') as string;
  const autoExec = fullRule?.auto_execute ?? false;

  // Keep derived-value refs in sync (placed AFTER the derived consts to avoid TDZ)
  useEffect(() => {
    ruleLatRef.current  = ruleLat;
    ruleLngRef.current  = ruleLng;
    rulePosRef.current  = ruleLat != null && ruleLng != null ? { lat: ruleLat, lng: ruleLng } : null;
  }, [ruleLat, ruleLng]);
  useEffect(() => { ruleRRef.current = ruleR; }, [ruleR]);
  useEffect(() => { ruleTypeRef.current = ruleType; autoExecRef.current = autoExec; }, [ruleType, autoExec]);

  // ── Map init ────────────────────────────────────────────────────────────────
  const initMap = useCallback(() => {
    if (!mapContainerRef.current || mapRef.current || !MAPBOX_TOKEN) return;

    // Sync refs before building the map so setupMapLayers has fresh values
    ruleLatRef.current          = ruleLat;
    ruleLngRef.current          = ruleLng;
    ruleRRef.current            = ruleR;
    ruleTypeRef.current         = ruleType;
    autoExecRef.current         = autoExec;
    isDarkRef.current           = false; // default light
    allowedCountriesRef.current = allowedCountries;

    const cLng = ruleLng ?? userPos?.lng ?? -118.24;
    const cLat = ruleLat ?? userPos?.lat ?? 34.05;

    const m = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center: [cLng, cLat],
      zoom: ruleLat != null ? 12 : 4,
      pitch: 20, bearing: 0,
      attributionControl: false,
      accessToken: MAPBOX_TOKEN,
    });
    mapRef.current = m;

    m.addControl(new mapboxgl.NavigationControl({ visualizePitch: true }), 'top-right');
    m.addControl(new mapboxgl.FullscreenControl(), 'top-right');
    m.addControl(new mapboxgl.AttributionControl({ compact: true }), 'bottom-right');

    m.once('load', () => {
      setupMapLayers(m);

      // Apply country borders immediately using cached refs — this catches the
      // common case where countriesGeoJson was already loaded before initMap ran.
      // The dedicated applyCountryLayers effect handles the case where GeoJSON
      // arrives AFTER the map loads.  allowedCountries is optional (grey fallback).
      const _rawGeoJson = countriesGeoJsonRef.current;
      const _rawAC      = allowedCountriesRef.current;
      if (_rawGeoJson) {
        applyCountryLayers(m, buildCountriesData(_rawGeoJson, _rawAC ?? undefined));
      }

      // ── Country hover tooltip ─────────────────────────────────────────────
      // Resolve country name + ISO2 from a map pixel point.
      // Uses our custom countries-fill layer (full country area) first, then
      // falls back to the built-in country-label layer (label areas only).
      const resolveCountryAtPoint = (pt: mapboxgl.Point): HoveredCountry | null => {
        if (m.getLayer('countries-fill')) {
          const tightBox: [mapboxgl.PointLike, mapboxgl.PointLike] = [
            [pt.x - 2, pt.y - 2], [pt.x + 2, pt.y + 2],
          ];
          const fillFeatures = m.queryRenderedFeatures(tightBox, { layers: ['countries-fill'] });
          if (fillFeatures.length > 0) {
            const f    = fillFeatures[0];
            const iso3 = typeof f.id === 'string' ? f.id : '';
            const iso2 = iso3 ? iso3ToIso2(iso3) : '';
            const nm   = (f.properties?.name || '') as string;
            if (nm) return { name: nm, iso2, x: pt.x, y: pt.y };
          }
        }
        const labelBox: [mapboxgl.PointLike, mapboxgl.PointLike] = [
          [pt.x - 40, pt.y - 40], [pt.x + 40, pt.y + 40],
        ];
        const labelFeatures = m.queryRenderedFeatures(labelBox, { layers: ['country-label'] });
        if (labelFeatures.length > 0) {
          const f   = labelFeatures[0];
          const iso = ((f.properties?.iso_3166_1 || f.properties?.iso_3166_1_alpha_2 || '') as string).toUpperCase();
          const nm  = (f.properties?.name_en || f.properties?.name || '') as string;
          if (nm) return { name: nm, iso2: iso, x: pt.x, y: pt.y };
        }
        return null;
      };

      // ── Desktop vs Mobile tooltip behaviour ───────────────────────────────
      // On touch devices:  tap to show, tap same country again (or elsewhere) to hide.
      //                    mousemove events are NOT used (they fire unreliably on touch).
      // On desktop:        hover-in shows immediately; hover-out hides after 1-second
      //                    delay so the user can move the cursor onto the tooltip.
      const IS_TOUCH = navigator.maxTouchPoints > 0;

      if (!IS_TOUCH) {
        // Desktop — hover
        m.on('mousemove', e => {
          if (hovTimerRef.current) { clearTimeout(hovTimerRef.current); hovTimerRef.current = null; }
          const country = resolveCountryAtPoint(e.point);
          setHovCtryRef.current?.(country);
        });
        m.on('mouseleave', () => {
          hovTimerRef.current = setTimeout(() => {
            hovTimerRef.current = null;
            setHovCtryRef.current?.(null);
          }, 1000);
        });
      }

      // Tap (mobile) OR click (desktop as secondary) — toggle tooltip
      m.on('click', e => {
        // On desktop the primary mechanism is hover; clicks on the map body clear the tooltip
        // (but clicks on the tooltip itself are stopped via stopPropagation).
        if (!IS_TOUCH) {
          setHovCtryRef.current?.(null);
          return;
        }
        // Mobile: detect country and toggle
        const country = resolveCountryAtPoint(e.point);
        if (country) {
          setHovCtryRef.current?.((prev: HoveredCountry | null) =>
            prev?.name === country.name ? null : country
          );
        } else {
          setHovCtryRef.current?.(null);
        }
      });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [MAPBOX_TOKEN, ruleLat, ruleLng, ruleR, ruleType, autoExec, userPos, allowedCountries, setupMapLayers]);

  useEffect(() => {
    if (isOpen) {
      const t = setTimeout(initMap, 120);
      return () => clearTimeout(t);
    } else {
      mapRef.current?.remove();
      mapRef.current = null;
      contractMarkerRef.current = null;
      userMarkerRef.current     = null;
      setHoveredCountry(null);
    }
  }, [isOpen, initMap]);

  // ── Dark/light toggle — re-apply style and rebuild all layers ───────────────
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    isDarkRef.current = overlayMapStyle === 'dark';
    const styleUrl = overlayMapStyle === 'dark'
      ? 'mapbox://styles/mapbox/dark-v11'
      : 'mapbox://styles/mapbox/light-v11';
    m.setStyle(styleUrl);
    m.once('style.load', () => {
      setupMapLayers(m);
      // Re-apply country borders after style change (setupMapLayers no longer does this)
      const raw = countriesGeoJsonRef.current;
      const ac  = allowedCountriesRef.current;
      if (raw) applyCountryLayers(m, buildCountriesData(raw, ac ?? undefined));
    });
  }, [overlayMapStyle, setupMapLayers]);

  // ── Radius fix — update circle source when fullRule loads ──────────────────
  // initMap draws the circle immediately with whatever ruleR is at that moment
  // (default 500 m when fullRule hasn't loaded yet).  This effect patches the
  // source once we get the real radius from the API.
  useEffect(() => {
    const m = mapRef.current;
    if (!m || !m.isStyleLoaded() || ruleLat == null || ruleLng == null) return;
    ruleRRef.current = ruleR;
    const src = m.getSource('rule-circle') as mapboxgl.GeoJSONSource | undefined;
    if (src) {
      src.setData(makeCircle(ruleLng, ruleLat, ruleR) as any);
    }
  }, [fullRule, ruleR, ruleLat, ruleLng]);

  // ── Marker type fix — swap contract marker element when ruleType/autoExec ──
  // change (e.g. when fullRule arrives and reveals the real rule_type).
  // setupMapLayers bakes in the type at map-load time, so we must replace the
  // marker's DOM element here if it has already been created.
  useEffect(() => {
    const m = mapRef.current;
    if (!m || !contractMarkerRef.current) return;
    ruleTypeRef.current  = ruleType;
    autoExecRef.current  = autoExec;
    // Replace the element in-place — preserves lng/lat without removing/re-adding
    const newEl = makeContractMarkerEl(ruleType, autoExec);
    contractMarkerRef.current.getElement().replaceWith(newEl);
    // Mapbox holds a reference to the old element; update it via the internal
    // _element property so subsequent marker operations use the new element.
    (contractMarkerRef.current as any)._element = newEl;
  }, [ruleType, autoExec]);

  if (!isOpen) return null;

  const distance = userPos && ruleLat != null && ruleLng != null
    ? haversineKm(userPos.lat, userPos.lng, ruleLat, ruleLng)
    : null;

  // Zoom handlers use refs — never stale
  const zoomToContract = () => {
    const m = mapRef.current, p = rulePosRef.current;
    if (!m || !p) return;
    m.flyTo({ center: [p.lng, p.lat], zoom: 14, pitch: 55, bearing: -15, duration: 1600, essential: true });
  };
  const zoomToMe = () => {
    const m = mapRef.current, p = userPosRef.current;
    if (!m || !p) return;
    m.flyTo({ center: [p.lng, p.lat], zoom: 15, pitch: 55, bearing: 0, duration: 1600, essential: true });
  };
  const zoomOut = () => {
    const m = mapRef.current;
    if (!m) return;
    const bounds = new mapboxgl.LngLatBounds();
    if (rulePosRef.current) bounds.extend([rulePosRef.current.lng, rulePosRef.current.lat]);
    if (userPosRef.current) bounds.extend([userPosRef.current.lng, userPosRef.current.lat]);
    if (!bounds.isEmpty()) {
      m.fitBounds(bounds, { padding: 90, maxZoom: 14, duration: 1400, essential: true });
    } else if (rulePosRef.current) {
      m.flyTo({ center: [rulePosRef.current.lng, rulePosRef.current.lat], zoom: 4, pitch: 0, duration: 1400, essential: true });
    }
  };

  // Status badge — when opened from a live map marker (no status supplied) the
  // rule is simply active/live, not a pending deposit awaiting settlement.
  const statusColor =
    info.status === 'completed' ? C.green :
    info.status === 'rejected'  ? C.red   :
    info.status === 'pending'   ? C.amber :
    C.blue; // no status → active rule
  const statusLabel =
    info.status === 'completed' ? '✓ Completed' :
    info.status === 'rejected'  ? '✕ Rejected'  :
    info.status === 'pending'   ? '⏳ Pending'   :
    '⚡ Active';

  const ruleColour  = RULE_BORDERS[ruleType] || RULE_BORDERS.location;
  const ruleGradient = RULE_GRADIENTS[ruleType] || RULE_GRADIENTS.location;

  const meta: [string, React.ReactNode][] = [
    ['Rule Name',    info.ruleName || '—'],
    ['Rule ID',      String(info.ruleId)],
    ['Status',       <span style={{ color: statusColor, fontWeight: 700 }}>{statusLabel}</span>],
    ['Function',     info.functionName || '—'],
    ['Contract',     info.contractAddress
      ? <span style={{ fontFamily: 'monospace', fontSize: '11px', wordBreak: 'break-all' }}>{info.contractAddress}</span>
      : '—'],
    ['Network',      info.network || network],
    ...(fullRule ? [
      ['Rule Type',  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                       <span style={{ width: 8, height: 8, borderRadius: 2, background: ruleColour, display: 'inline-block' }} />
                       {(fullRule.rule_type || '—').charAt(0).toUpperCase() + (fullRule.rule_type || '').slice(1)}
                     </span>] as [string, React.ReactNode],
      ['Trigger',    fullRule.trigger_on || '—'] as [string, React.ReactNode],
      ['Radius',     fullRule.radius_meters != null ? `${fullRule.radius_meters} m` : (info.radiusMeters != null ? `${info.radiusMeters} m` : '—')] as [string, React.ReactNode],
      ['Auto-Execute', fullRule.auto_execute ? <span style={{ color: C.goldDark, fontWeight: 700 }}>⚡ Yes</span> : 'No'] as [string, React.ReactNode],
      ['Req. Confirm', fullRule.requires_confirmation ? <span style={{ color: C.blue, fontWeight: 600 }}>✓ Yes</span> : 'No'] as [string, React.ReactNode],
      ...(fullRule.max_executions_per_public_key != null
        ? [['Max Executions', `${fullRule.max_executions_per_public_key} / ${fmtSec(fullRule.execution_time_window_seconds ?? 86400)}`] as [string, React.ReactNode]]
        : []),
      ...(fullRule.min_location_duration_seconds != null
        ? [['Min Dwell', fmtSec(fullRule.min_location_duration_seconds)] as [string, React.ReactNode]]
        : []),
      ['Active', fullRule.is_active
        ? <span style={{ color: C.green, fontWeight: 600 }}>● Active</span>
        : <span style={{ color: C.textFaint }}>○ Inactive</span>] as [string, React.ReactNode],
    ] : [
      // fullRule not loaded yet — show what we already know from the nearby-contract data
      ...((info.ruleType) ? [['Rule Type', <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                              <span style={{ width: 8, height: 8, borderRadius: 2, background: ruleColour, display: 'inline-block' }} />
                                              {info.ruleType.charAt(0).toUpperCase() + info.ruleType.slice(1)}
                                            </span>] as [string, React.ReactNode]] : []),
      ...((info.radiusMeters != null) ? [['Radius', `${info.radiusMeters} m`] as [string, React.ReactNode]] : []),
    ]),
    ['Geofence',     ruleLat != null ? `${ruleLat.toFixed(5)}, ${ruleLng?.toFixed(5)}` : '—'],
    ...(distance != null ? [['Distance', <strong style={{ color: C.blue }}>{fmtDist(distance)}</strong>] as [string, React.ReactNode]] : []),
    ...(info.matchedAt   ? [['Matched At',   new Date(info.matchedAt).toLocaleString()]   as [string, React.ReactNode]] : []),
    ...(info.completedAt ? [['Completed At', new Date(info.completedAt).toLocaleString()] as [string, React.ReactNode]] : []),
    ...(info.rejectedAt  ? [['Rejected At',  new Date(info.rejectedAt).toLocaleString()]  as [string, React.ReactNode]] : []),
    ...(info.rejectedReason ? [['Reject Reason', info.rejectedReason] as [string, React.ReactNode]] : []),
    ...(info.transactionHash ? [['Tx Hash',
      <a href={`https://stellar.expert/explorer/${network}/tx/${info.transactionHash}`}
         target="_blank" rel="noopener noreferrer"
         style={{ color: C.blue, fontFamily: 'monospace', fontSize: '11px', wordBreak: 'break-all' }}>
        {info.transactionHash.slice(0, 16)}…↗
      </a>
    ] as [string, React.ReactNode]] : []),
  ];

  const mapBtnStyle: React.CSSProperties = {
    padding: '5px 11px', fontSize: '11px', fontWeight: 600, borderRadius: '6px',
    border: '1px solid rgba(255,255,255,0.18)', background: 'rgba(10,15,30,0.7)',
    color: '#fff', cursor: 'pointer', backdropFilter: 'blur(6px)',
    display: 'flex', alignItems: 'center', gap: 4, transition: 'background .15s',
    lineHeight: 1.4,
  };
  const mapBtnDisabled: React.CSSProperties = { ...mapBtnStyle, opacity: 0.38, cursor: 'not-allowed' };

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: C.bg, borderRadius: '14px', width: '100%', maxWidth: '560px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.5)' }}
      >
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: `1px solid ${C.border}`, background: '#1a1f2e', color: '#fff', flexShrink: 0 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: '15px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: '18px', width: 28, height: 28, borderRadius: '5px', background: ruleGradient, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>🧮</span>
              {info.ruleName || `Rule ${info.ruleId}`}
            </div>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '2px' }}>Rule Match Details · {network}</div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', fontSize: '18px', cursor: 'pointer', width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>×</button>
        </div>

        {/* ── Map ────────────────────────────────────────────────────────── */}
        <div style={{ position: 'relative', height: '280px', flexShrink: 0, background: '#0d1120' }}>
          {/* Add "rmd-map" class so CSS hides the Mapbox logo */}
          <div ref={mapContainerRef} className="rmd-map" style={{ width: '100%', height: '100%' }} />

          {/* ── Country tooltip — Portal renders it INSIDE the map container ───
               This is critical for fullscreen: when Mapbox's FullscreenControl
               gives the container position:fixed, children rendered via Portal
               follow the container, unlike React siblings which stay behind. */}
          {mapContainerRef.current && hoveredCountry && createPortal(
            (() => {
              const MAX_W = 220, PAD = 10;
              const containerW = mapContainerRef.current?.offsetWidth ?? 400;
              const containerH = mapContainerRef.current?.offsetHeight ?? 280;
              const x = Math.min(Math.max(hoveredCountry.x, PAD), containerW - MAX_W - PAD);
              const y = hoveredCountry.y > containerH * 0.55
                ? hoveredCountry.y - 90
                : hoveredCountry.y + 18;
              return (
                <div
                  onMouseEnter={() => {
                    if (hovTimerRef.current) { clearTimeout(hovTimerRef.current); hovTimerRef.current = null; }
                  }}
                  onMouseLeave={() => {
                    hovTimerRef.current = setTimeout(() => {
                      hovTimerRef.current = null;
                      setHoveredCountry(null);
                    }, 1000);
                  }}
                  onClick={e => e.stopPropagation()}
                  style={{
                    position: 'absolute', left: x, top: y,
                    width: MAX_W,
                    zIndex: 999,
                    background: 'rgba(8,13,24,0.97)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderLeft: '3px solid #4CAF50',
                    borderRadius: '10px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
                    backdropFilter: 'blur(12px)',
                    overflow: 'hidden',
                    pointerEvents: 'auto',
                  }}
                >
                  {/* Header */}
                  <div style={{ padding: '9px 11px 8px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      {hoveredCountry.iso2 && (
                        <img
                          src={getFlagUrl(hoveredCountry.iso2)}
                          alt={hoveredCountry.iso2}
                          style={{ width: 20, height: 'auto', borderRadius: 2, flexShrink: 0 }}
                          onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                      )}
                      <span style={{ fontWeight: 700, fontSize: '13px', color: '#f0f4ff', lineHeight: 1.2 }}>{hoveredCountry.name}</span>
                      {hoveredCountry.iso2 && (
                        <span style={{ fontSize: '10px', fontWeight: 700, color: '#94a3b8', background: 'rgba(255,255,255,0.1)', borderRadius: 4, padding: '1px 5px', letterSpacing: '0.06em' }}>
                          {hoveredCountry.iso2}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => setHoveredCountry(null)}
                      style={{ background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: '50%', width: 20, height: 20, color: '#94a3b8', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                    >✕</button>
                  </div>

                  {/* Nav row */}
                  <div style={{ padding: '7px 10px', display: 'flex', gap: 5, flexWrap: 'wrap' as const }}>
                    {[
                      { icon: '+',  title: 'Zoom in',    fn: () => mapRef.current?.zoomIn({ duration: 500 }) },
                      { icon: '−',  title: 'Zoom out',   fn: () => mapRef.current?.zoomOut({ duration: 500 }) },
                      { icon: '🌍', title: 'World view', fn: () => mapRef.current?.flyTo({ center: [0, 20], zoom: 2, pitch: 0, bearing: 0, speed: 1.2, essential: true }) },
                      { icon: '📍', title: 'My location', fn: zoomToMe },
                      { icon: '⛶', title: 'Reset pitch', fn: () => mapRef.current?.easeTo({ pitch: 0, bearing: 0, duration: 600, essential: true }) },
                    ].map(({ icon, title, fn }) => (
                      <button
                        key={icon} title={title} onClick={e => { e.stopPropagation(); fn(); }}
                        style={{
                          background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)',
                          borderRadius: '6px', color: '#e2e8f0', fontSize: '13px', cursor: 'pointer',
                          width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >{icon}</button>
                    ))}
                  </div>
                </div>
              );
            })(),
            mapContainerRef.current
          )}

          {/* Legend — bottom-left */}
          <div style={{ position: 'absolute', bottom: 10, left: 10, background: 'rgba(0,0,0,0.72)', borderRadius: '8px', padding: '7px 10px', fontSize: '11px', color: '#fff', display: 'flex', flexDirection: 'column', gap: 4, pointerEvents: 'none' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 12, height: 12, borderRadius: 3, background: ruleGradient, border: `1.5px solid ${ruleColour}`, display: 'inline-block' }} />
              Rule geofence {ruleLat != null ? `(${ruleR} m)` : ''}
            </div>
            {userPos && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 12, height: 12, background: C.gold, borderRadius: '50%', border: '2px solid #fff', display: 'inline-block' }} />
                Your location
              </div>
            )}
            {distance != null && <div style={{ color: C.gold, fontWeight: 700, marginTop: 2 }}>↔ {fmtDist(distance)}</div>}
            {!userPos && <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '10px' }}>GPS unavailable</div>}
          </div>
        </div>

        {/* ── Map controls bar ───────────────────────────────────────────── */}
        <div style={{ display: 'flex', gap: 8, padding: '8px 12px', background: '#111827', borderBottom: `1px solid ${C.border}`, flexShrink: 0, flexWrap: 'wrap' as const }}>
          <button style={rulePosRef.current ? mapBtnStyle : mapBtnDisabled} onClick={zoomToContract}>
            🧮 Zoom to Contract
          </button>
          <button style={userPosRef.current ? mapBtnStyle : mapBtnDisabled} onClick={zoomToMe}>
            📍 Zoom to Me
          </button>
          <button style={mapBtnStyle} onClick={zoomOut} title="Fit both markers in view">
            🔭 Fit Both
          </button>
          {/* Dark/light toggle */}
          <button
            style={{ ...mapBtnStyle, marginLeft: 'auto' }}
            title={overlayMapStyle === 'dark' ? 'Switch to light map' : 'Switch to dark map'}
            onClick={() => setOverlayMapStyle(s => s === 'dark' ? 'light' : 'dark')}
          >
            {overlayMapStyle === 'dark' ? '☀️' : '🌙'}
          </button>
          {distance != null && (
            <span style={{ fontSize: '12px', color: C.gold, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4, paddingRight: 4 }}>
              ↔ {fmtDist(distance)}
            </span>
          )}
        </div>

        {/* ── Metadata ───────────────────────────────────────────────────── */}
        <div style={{ overflowY: 'auto', padding: '14px 18px', flex: 1 }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: C.textFaint, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 10 }}>Rule Information</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <tbody>
              {meta.map(([label, value]) => (
                <tr key={String(label)} style={{ borderBottom: `1px solid ${C.border}` }}>
                  <td style={{ padding: '6px 0', color: C.textFaint, width: '38%', verticalAlign: 'top', paddingRight: 10 }}>{label}</td>
                  <td style={{ padding: '6px 0', color: C.text, fontWeight: 500 }}>{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ── Footer ─────────────────────────────────────────────────────── */}
        <div style={{ padding: '10px 18px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
          <button onClick={onClose} style={{ padding: '8px 22px', borderRadius: '8px', border: 'none', background: '#1a1f2e', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '13px' }}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default RuleMatchDetailOverlay;
